import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import {
  AppTourTooltip,
  WelcomeTooltip,
  CloseButton,
  VerticalLine,
  TourButton,
  ArrowTooltip,
  Content,
  Footer,
} from './styles';
import { AppStoreContext } from './appStoreContext';
import { firstLoginComplete } from '../utils/services/authAPI';
import { withTranslation } from 'react-i18next';
import { APP_TOUR } from './translationValues';
const NAMESPACES = ['APP_TOUR'];

export const AppTourContext = createContext();

const AppTourProvider = ({ children, t }) => {
  const { userDetails } = useContext(AppStoreContext);
  const [firstLogin, setFirstLogin] = useState(false);
  const [isTourActive, setIsTourActive] = useState(false);
  const [runAppTour, setRunAppTour] = useState(false);
  const [steps, setSteps] = useState([]);
  const [stepIndex, setStepIndex] = useState(0);
  const [data, setData] = useState({}); // This state holds all the necessary data required for validation
  const [tourSkipped, setTourSkipped] = useState(false); // This state is used to show tooltips on the landing page when the user skips the tour
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const tooltips = [
    {
      target: '#scrollContainer',
      content: '',
      placement: 'center',
      hideFooter: true,
    },
    {
      target: '.app_button',
      content: t(APP_TOUR.TOOLTIP_TITLE_START_DESIGNING.key, APP_TOUR.TOOLTIP_TITLE_START_DESIGNING.value),
      placement: 'left',
      hideFooter: true,
      styles: {
        options: {
          arrowColor: 'transparent',
        },
      },
    },
    {
      target: '.template-name-input',
      content: t(APP_TOUR.TOOLTIP_TITLE_TEMPLATE_NAME.key, APP_TOUR.TOOLTIP_TITLE_TEMPLATE_NAME.value),
      hideSkipButton: true,
      hideBackButton: true,
      validation: true,
    },
    {
      target: '.template-category-input',
      content: t(APP_TOUR.TOOLTIP_TITLE_CATEGORY.key, APP_TOUR.TOOLTIP_TITLE_CATEGORY.value),
      hideSkipButton: true,
      validation: true,
    },
    {
      target: '.template-team-input',
      content: t(APP_TOUR.TOOLTIP_TITLE_TEAM.key, APP_TOUR.TOOLTIP_TITLE_TEAM.value),
      hideSkipButton: true,
      validation: true,
    },
    {
      target: '.master-template',
      content: t(APP_TOUR.TOOLTIP_TITLE_MASTER_TEMPLATES.key, APP_TOUR.TOOLTIP_TITLE_MASTER_TEMPLATES.value),
      hideNextButton: true,
    },
    {
      target: '.master-template-section',
      content: t(APP_TOUR.TOOLTIP_TITLE_MASTER_TEMPLATES_SELECTION.key, APP_TOUR.TOOLTIP_TITLE_MASTER_TEMPLATES_SELECTION.value),
      placement: 'left',
      hideBackButton: true,
      validation: true,
    },
    {
      target: '.form_btn',
      content: t(APP_TOUR.TOOLTIP_TITLE_CREATE_TEMPLATE_BUTTON.key, APP_TOUR.TOOLTIP_TITLE_CREATE_TEMPLATE_BUTTON.value),
      placement: 'top',
      hideSkipButton: true,
      hideNextButton: true,
    },
    {
      target: '#complex-page-wrapper',
      content: t(APP_TOUR.TOOLTIP_TITLE_TEMPLATE_CREATED.key, APP_TOUR.TOOLTIP_TITLE_TEMPLATE_CREATED.value),
      placement: 'center',
      hideFooter: true,
    },
  ];

  useEffect(() => {
    setSteps(tooltips);
  }, [t]);

  useEffect(() => {
    // Close the last tooltip automatically after 5 seconds
    let timer;
    if (stepIndex === steps.length - 1) {
      timer = setTimeout(() => {
        handleTourSkip();
      }, 5000);
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [stepIndex]);  

  // Update the value of the validation field in the steps array to enable or disable the 'Next' button
  const handleTourValidation = useCallback(value => {
    const updatedArray = [...steps];
    updatedArray[stepIndex] = {
      ...updatedArray[stepIndex],
      validation: value,
    };
    setSteps(updatedArray);
  }, [stepIndex]);

  const handleTourSkip = useCallback(() => {
    // Add an extra class to animate the tooltip when it closes
    const floater = document.querySelector('.__floater__open');
    if (floater) {
      floater.classList.add('app_tour_close_animation');
    }
    // Set a timeout to disable the app tour after the animation is complete
    setTimeout(() => {
      setFirstLogin(false);
      setIsTourActive(false);
      setRunAppTour(false);
      if(stepIndex === 0) {
        setTourSkipped(true);
      }
      setStepIndex(0);
      // Trigger the API to set the firstLogin value to false in the backend
      firstLoginComplete();
    }, 300);
  }, [stepIndex]);

  const previousStep = useCallback(() => {
    setStepIndex(prevIndex => {
      if (stepIndex === 5 && data.teamLength === 1) {
        return 3;
      }
      if(stepIndex === 7) {
        return data.masterTemplateSelected ? 6 : 5;
      }
      return prevIndex - 1;
    });
  }, [stepIndex]);

  const nextStep = useCallback(() => {
    setStepIndex(prevIndex => {
      if (stepIndex === 2) {
        return data.tempNameExists ? 7 : prevIndex + 1;
      }
      if (stepIndex === 3) {
        return data.teamLength === 1 ? 5 : prevIndex + 1;
      }
      return prevIndex + 1;
    });
  }, [stepIndex, data]);

  const Tooltip = ({ backProps, index, isLastStep, step, tooltipProps }) => {
    const customTooltips = [0, 1];
    const displayName = userDetails?.displayName;

    return (
      <AppTourTooltip {...tooltipProps} welcomeTooltip={index === 0} customTooltip={index === 1}>
        {!(index === 1) && (
          <CloseButton onClick={handleTourSkip}>
            <span>&times;</span>
          </CloseButton>
        )}

        {index === 0 && (
          <WelcomeTooltip>
            <div className="column">
              <h3>
                {t(APP_TOUR.WELCOME_MESSAGE.key, APP_TOUR.WELCOME_MESSAGE.value, { displayName })}<br />
                {t(APP_TOUR.WELCOME_MESSAGE_DESCRIPTION.key, APP_TOUR.WELCOME_MESSAGE_DESCRIPTION.value)}
              </h3>
              <p>{t(APP_TOUR.WELCOME_MESSAGE_INTRODUCTION.key, APP_TOUR.WELCOME_MESSAGE_INTRODUCTION.value)}</p>
              <div>
                <TourButton skip onClick={handleTourSkip}>
                  {t(APP_TOUR.SKIP_TOUR_BUTTON.key, APP_TOUR.SKIP_TOUR_BUTTON.value)}
                </TourButton>
                <TourButton next onClick={() => setStepIndex(prevIndex => prevIndex + 1)}>
                  {t(APP_TOUR.TAKE_TOUR_BUTTON.key, APP_TOUR.TAKE_TOUR_BUTTON.value)}
                </TourButton>
              </div>
            </div>
            <VerticalLine />
            <div className="column">
              <p>{t(APP_TOUR.CHECKOUT_DEMO_VIDEO_MESSAGE.key, APP_TOUR.CHECKOUT_DEMO_VIDEO_MESSAGE.value)}</p>
              <iframe
                width="380"
                height="215"
                src={process.env.REACT_APP_TOUR_VIDEO_LINK}
                title="Document Template Creation"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen={true}
              ></iframe>
            </div>
          </WelcomeTooltip>
        )}

        {index === 1 && (
          <ArrowTooltip>
            <div>{step.content}</div>
            <svg width="100" height="100" xmlns="http://www.w3.org/2000/svg">
              <path d="M 10 50 Q 50 30 90 50" stroke="black" fill="transparent" stroke-width="2" />
            </svg>
          </ArrowTooltip>
        )}

        {step.content && !customTooltips.includes(index) && (
          <Content>
            {step.content}
            {isLastStep && (
              <svg
                width="100px"
                height="100px"
                viewBox="0 0 512 512"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
                role="img"
                class="iconify iconify--fxemoji"
                preserveAspectRatio="xMidYMid meet"
              >
                <path
                  fill="#D8B09A"
                  d="M442.3 391.6l-2.3-2.3l-16.6-16.6c11.1-13.9 15.3-31.4 12.8-48.1c0-.3-.1-.6-.2-.9c-.1-.4-.1-.7-.2-1.1c-.1-1.3-.6-3.3-1.4-5.9c-.4-1.3-.8-2.5-1.2-3.8c-8.9-26.1-36.5-93.1-31.7-159.1c.1-14.8-9.8-28.2-23.8-27.4c-14 .8-39.5 8.1-40.9 74.1c-.2 12.2-1.9 18.1-1.4 28.1L175.7 68.9c-10.4-10.4-26.9-10.6-37.3-.2c-10.4 10.4-10.4 27.3-.1 37.6l94.5 94.5c.1.1.1.2.2.2c8.8 8.8-1.8 21-11.1 15.7l-113.1-113c-10.4-10.4-26.9-10.6-37.3-.2c-10.4 10.5-10.5 27.3-.1 37.7l113.4 113.4c5.5 8.3-5 18.3-13.2 11.6l-99-99c-10.4-10.4-26.9-10.6-37.3-.2c-10.4 10.4-10.5 27.2-.1 37.6l97.5 97.5c.1.2.3.5.6.8c8.5 8.5-1.6 20.2-10.6 15.3l-69.8-69.8c-10.4-10.4-26.9-10.6-37.3-.2c-10.4 10.4-10.5 27.2-.1 37.6L179.6 450c33.4 33.4 81.7 42.5 123.3 27.5l5.1 5.1l21.6 21.6c10.4 10.4 27.2 10.4 37.6 0l75.1-75.1c10.4-10.3 10.4-27.2 0-37.5z"
                ></path>
                <path
                  fill="#00BEEA"
                  d="M377.9 33c-.3-.9-.9-1.6-1.8-1.9l-29.9-10.9c-.9-.3-1.8-.2-2.6.3s-1.2 1.3-1.3 2.2l-4.6 59.2c-.1 1.3.7 2.5 1.9 2.9c.3.1.7.2 1 .2c.9 0 1.8-.4 2.4-1.2l34.5-48.3c.6-.7.7-1.6.4-2.5z"
                ></path>
                <path
                  fill="#00BEEA"
                  d="M439 90.6l-13.4-28.8c-.4-.8-1.1-1.4-2-1.6c-.9-.2-1.8 0-2.5.6L376 99.4c-1 .8-1.3 2.2-.7 3.4c.5 1 1.5 1.7 2.6 1.7h.5l58.5-9.7c.9-.1 1.7-.7 2.1-1.5c.4-.9.4-1.9 0-2.7z"
                ></path>
                <path
                  fill="#00BEEA"
                  d="M298 75.5L288.3 17c-.1-.9-.7-1.7-1.5-2.1c-.8-.4-1.7-.4-2.6-.1l-28.8 13.4c-.8.4-1.4 1.1-1.6 2c-.2.9 0 1.8.6 2.5L293 77.9c.6.7 1.4 1 2.2 1c.4 0 .8-.1 1.2-.3c1.2-.5 1.8-1.8 1.6-3.1z"
                ></path>
                <path
                  fill="#FFD3B6"
                  d="M500.4 391.6l-18.9-18.9c11.1-13.9 15.3-31.4 12.8-48.1c0-.3-.1-.6-.2-.9c-.1-.4-.1-.7-.2-1.1c-.1-1.3-.6-3.3-1.4-5.9c-.4-1.3-.8-2.5-1.2-3.8c-8.9-26.1-36.5-93.1-31.7-159.1c.1-14.8-9.8-28.2-23.8-27.4s-39.5 8.1-40.9 74.1c-.2 12.2-1.9 18.1-1.4 28.1L233.8 68.9c-10.4-10.4-26.9-10.6-37.3-.2c-10.4 10.4-10.4 27.3-.1 37.6l94.5 94.5c.1.1.1.2.2.2c8.8 8.8-1.8 21-11.1 15.7l-113.1-113c-10.4-10.4-26.9-10.6-37.3-.2c-10.4 10.4-10.5 27.2-.1 37.6l113.4 113.4c5.5 8.3-5 18.3-13.2 11.6l-99-99c-10.4-10.4-26.9-10.6-37.3-.2c-10.4 10.4-10.5 27.2-.1 37.6l97.5 97.5c.1.2.3.5.6.8c8.5 8.5-1.6 20.2-10.6 15.3L111 248.3c-10.4-10.4-26.9-10.6-37.3-.2c-10.4 10.4-10.5 27.2-.1 37.6L237.7 450c33.4 33.4 81.7 42.5 123.3 27.5l26.7 26.7c10.4 10.4 27.2 10.4 37.6 0l75.1-75.1c10.4-10.3 10.4-27.2 0-37.5z"
                ></path>
                <path
                  fill="#F2C7AA"
                  d="M481.3 372.4c-1.5 2.1-3 4-4.5 5.5L419.6 435v-.3L404.4 450c-12.7 12.7-27.5 21.8-43.3 27.5l23.8 23.8c5.2-1.6 9.6-3.8 12.4-6.6l93.9-93.9c2.7-2.7 5-6.8 6.9-11.6l-16.8-16.8z"
                ></path>
              </svg>
            )}
          </Content>
        )}

        {!step.hideFooter && (
          <Footer>
            {!step.hideSkipButton && (
              <TourButton skip onClick={() => setStepIndex(7)}>
                {t(APP_TOUR.SKIP_BUTTON.key, APP_TOUR.SKIP_BUTTON.value)}
              </TourButton>
            )}
            {!step.hideBackButton && (
              <TourButton {...backProps} onClick={previousStep}>
                {t(APP_TOUR.BACK_BUTTON.key, APP_TOUR.BACK_BUTTON.value)}
              </TourButton>
            )}
            {!step.hideNextButton && (
              <TourButton next onClick={nextStep} disabled={step.validation}>
                {t(APP_TOUR.NEXT_BUTTON.key, APP_TOUR.NEXT_BUTTON.value)}
              </TourButton>
            )}
          </Footer>
        )}
      </AppTourTooltip>
    );
  };

  return (
    <AppTourContext.Provider
      value={{
        firstLogin,
        setFirstLogin,
        runAppTour,
        setRunAppTour,
        steps,
        stepIndex,
        setStepIndex,
        Tooltip,
        handleTourValidation,
        data,
        setData,
        isTourActive,
        setIsTourActive,
        tourSkipped,
        setTourSkipped,
        tooltipVisible,
        setTooltipVisible,
      }}
    >
      {children}
    </AppTourContext.Provider>
  );
};

export default withTranslation(NAMESPACES)(AppTourProvider);
